/**
 * The M2 bridge's mining link (M3 plan §7 item 6, slice R22i):
 *
 * - whenever M2 takes a streamed rock out of the world (depleted, fractured or burst: `removeFieldRock` bumps
 *   `sim.fieldVersion`), the rock's key gets a 0 delta in `state.deltas[system].rocks`, so the streamer leaves it out
 *   of every later stream-in and save;
 * - on every system switch (the `m2-world` enter hook) M2's rocks and wrecks of the system left go into its deltas
 *   and the entered system's wrecks come back, and M2's derelict sections move onto the entered system's derelict
 *   POI (`src/bridge/m2/systemWorld.ts`; a derelict-graveyard system also gets its extra salvage sites, M8 MC-h2, which
 *   read the M7 story sites overlay, cached per galaxy seed), the entered system's unfinished seeded hulks spawn (M9
 *   CH-2c: `systemHulks` minus `career.hulks.done`, capped at `salvage.json hulks.maxLocal` unclaimed, an open claim's always, PG-2, from the same overlay),
 *   and in tetherstone-vein systems the fracture rocks of the anomaly
 *   hotspots carry tetherstone (`src/bridge/m2/hotspots.ts`), and the rock runtime gets the entered system's galaxy
 *   field mix lookup (`src/bridge/m2/fieldMix.ts`, MC-a5a; nothing reads it until MC-a5b), unbound on dispose.
 *
 * Nothing to do outside the Reach. `afterTick` is one comparison and four reference copies while no rock changes
 * (D-014).
 */
import type { GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { fieldMixOf } from '../../bridge/m2/fieldMix'
import { hotspotTetherChance, loadBridgeMiningConfig } from '../../bridge/m2/hotspots'
import { createRockDeltaSync, syncRockDeltas, type RockDeltaSync } from '../../bridge/m2/rocks'
import {
  createSystemWorldWatch,
  graveyardSalvageSites,
  openHulkKeys,
  salvageSiteOf,
  spawnSystemHulks,
  SYSTEM_WORLD_HOOK,
  switchSystemWorld,
  systemHulks,
  watchSystemWorld,
  type SystemWorldWatch,
} from '../../bridge/m2/systemWorld'
import { setSalvageSite } from '../../career/salvageOrigin'
import { logger } from '../../core/logger'
import { hulkTableOf } from '../../hulks/data'
import type { ReachRuntime } from '../../reach/runtime/types'
import type { RocksRuntime } from '../../space/contracts'
import { storySites, type StorySites } from '../../story-link/sites'

/** Right after the Reach feature (160), which runs the session's streaming and merge. */
const ORDER = FEATURE_ORDER.reach + 2

let sync: RockDeltaSync | null = null
let watch: SystemWorldWatch = createSystemWorldWatch()
const config = loadBridgeMiningConfig()
/** Sessions that already have the enter hook (a hook name registers once per session). */
const hooked = new WeakSet<object>()
/** The rock runtime holding the current system's field mix lookup, unbound on dispose. */
let mixBound: Pick<RocksRuntime, 'setFieldMixAt'> | null = null
/** The M7 story sites of the galaxy (by seed), for the graveyard sites (M8 MC-h2) and the seeded hulks (M9 CH-2c); null when the overlay failed. */
let sitesCache: { readonly seed: number; readonly sites: StorySites | null } | null = null

function storySitesOf(reach: ReachRuntime): StorySites | null {
  const seed = reach.galaxy.seed
  if (sitesCache === null || sitesCache.seed !== seed) {
    let sites: StorySites | null = null
    try {
      sites = storySites(reach.galaxy, (id) => reach.layoutOf(id))
    } catch (err: unknown) {
      logger.warn('bridge: the story sites overlay failed; no graveyard salvage sites and no seeded hulks', err)
    }
    sitesCache = { seed, sites }
  }
  return sitesCache.sites
}

const feature: GameFeature = {
  name: 'bridgeMining',
  order: ORDER,
  init(ctx: GameContext): void {
    const reach = ctx.reach
    sync = reach === null ? null : createRockDeltaSync(ctx.sim)
    watch = createSystemWorldWatch()
    if (reach === null || hooked.has(reach.session)) return
    hooked.add(reach.session)
    reach.session.addEnterHook(SYSTEM_WORLD_HOOK, (layout) => {
      const m2 = ctx.services.maybe('m2')
      if (m2 === undefined) return
      const site = salvageSiteOf(layout)
      // M8 MC-h2: only a graveyard system with a salvage site builds the story overlay (once per galaxy seed).
      const extras =
        layout.archetype === 'derelict-graveyard' && site !== null
          ? graveyardSalvageSites(layout, site, { galaxySeed: reach.galaxy.seed, story: storySitesOf(reach) }, m2.data.salvage.graveyardSites)
          : []
      switchSystemWorld(watch, m2, reach.state, layout.systemId, site, extras)
      setSalvageSite(site?.poiId ?? null, layout.home?.harrowPoiId ?? null)
      // M9 CH-2c: after the switch (its import dropped the old system's hulks), the entered system's unfinished ones.
      // PG-2 (R2-4): an open claim's hulk always spawns, outside `hulks.maxLocal`.
      const career = m2.career()
      const open = openHulkKeys(career.hulks?.sites)
      const hulks = systemHulks(reach.galaxy, layout, storySitesOf(reach), hulkTableOf(career), career.hulks?.done, m2.data.salvage.hulks.maxLocal, open)
      if (hulks.length > 0) spawnSystemHulks(m2.space.salvage, hulks, (id) => ctx.sim.defs.findIndex((d) => d.id === id), open)
      m2.space.rocks.setTetherstoneChance(hotspotTetherChance(layout, ctx.sim.data.field, config.hotspotTetherstoneChance))
      if (m2.space.rocks.setFieldMixAt !== undefined) {
        // PF-4: the galaxy's signature ore(s), so an ice field in a Ferrite · Ice system scans ICE (the layout has none).
        const signature = reach.galaxy.systems.find((sys) => sys.id === layout.systemId)?.signature
        const mixAt = fieldMixOf(signature === undefined ? layout : { ...layout, signature }, ctx.sim.data.field)
        watch.mixAt = mixAt // grades the rock fractions written when the player leaves (MC-a6)
        m2.space.rocks.setFieldMixAt(mixAt)
        mixBound = m2.space.rocks
      }
    })
  },
  afterTick(ctx: GameContext): void {
    const reach = ctx.reach
    if (sync === null || reach === null) return
    const session = reach.session
    const layout = session.layout
    if (layout === null) return
    syncRockDeltas(sync, ctx.sim, session.fieldKeys, reach.state, layout.systemId)
    if (session.ready) watchSystemWorld(watch, layout.systemId, ctx.sim.field, session.fieldKeys, reach.state)
  },
  dispose(): void {
    mixBound?.setFieldMixAt?.(null)
    mixBound = null
  },
}

export default feature
